import React, { useEffect, useState } from 'react';
import { api, fmtCurrency, fmtDate, API_BASE } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';

// Stable per-row id generator for invoice line-items (used only for React keys)
const _uid = () => (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const _initialItems = () => [{ _rid: _uid(), description: '', quantity: 1, unit_price: 0 }];

export default function InvoicesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    client_name: '', client_address: '', client_email: '', invoice_number: '',
    currency: 'USD', notes: '', due_date: '',
    items: _initialItems(),
  });
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/b2b/invoices').then(r => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const total = form.items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { _rid: _uid(), description: '', quantity: 1, unit_price: 0 }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const setItem = (i, k, v) => setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));

  const create = async () => {
    setBusy(true);
    try {
      const payload = { ...form, items: form.items.map(({ _rid, ...it }) => ({ ...it, quantity: Number(it.quantity), unit_price: Number(it.unit_price) })) };
      const r = await api.post('/b2b/invoices', payload);
      toast.success(`Invoice ${r.data.invoice_number} created`);
      setOpen(false);
      setForm({ client_name: '', client_address: '', client_email: '', invoice_number: '', currency: 'USD', notes: '', due_date: '', items: _initialItems() });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const downloadPdf = async (id, invNumber) => {
    try {
      const r = await api.get(`/b2b/invoices/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${invNumber}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (err) { toast.error('Failed to download'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">Invoices</h1>
        <Button onClick={() => setOpen(true)} data-testid="invoice-create-button" className="bg-[hsl(var(--brand-navy))]"><Plus className="h-4 w-4 mr-1" />New invoice</Button>
      </div>

      <Card className="bg-white overflow-hidden">
        {items.length === 0 ? <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No invoices yet</div> : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--secondary))]"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Number</th><th className="p-3 text-left">Client</th><th className="p-3 text-right">Total</th><th className="p-3 text-right">Actions</th></tr></thead>
            <tbody>
              {items.map(inv => (
                <tr key={inv.id} className="border-t">
                  <td className="p-3 text-xs">{fmtDate(inv.created_at)}</td>
                  <td className="p-3 font-medium">{inv.invoice_number}</td>
                  <td className="p-3">{inv.client_name}</td>
                  <td className="p-3 text-right tabular-nums">{fmtCurrency(inv.total, inv.currency)}</td>
                  <td className="p-3 text-right"><Button size="sm" variant="outline" onClick={() => downloadPdf(inv.id, inv.invoice_number)} data-testid={`invoice-pdf-${inv.invoice_number}`}><Download className="h-4 w-4 mr-1" />PDF</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Client name *</Label><Input required data-testid="invoice-client-name" value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} /></div>
              <div><Label>Client email</Label><Input type="email" value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Client address</Label><Input value={form.client_address} onChange={e => setForm({ ...form, client_address: e.target.value })} /></div>
              <div><Label>Currency</Label><Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem><SelectItem value="KMF">KMF</SelectItem><SelectItem value="KZT">KZT</SelectItem></SelectContent></Select></div>
              <div><Label>Invoice number (optional)</Label><Input value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} placeholder="Auto-generated if empty" /></div>
              <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label>Items</Label><Button size="sm" variant="outline" onClick={addItem}><Plus className="h-4 w-4 mr-1" />Add</Button></div>
              {form.items.map((it, i) => (
                <div key={it._rid || `row-${i}`} className="grid grid-cols-12 gap-2 mb-2">
                  <Input className="col-span-6" placeholder="Description" data-testid={`invoice-item-desc-${i}`} value={it.description} onChange={e => setItem(i, 'description', e.target.value)} />
                  <Input className="col-span-2 tabular-nums" type="number" step="0.01" min="0" placeholder="Qty" value={it.quantity} onChange={e => setItem(i, 'quantity', e.target.value)} />
                  <Input className="col-span-3 tabular-nums" type="number" step="0.01" min="0" placeholder="Unit price" value={it.unit_price} onChange={e => setItem(i, 'unit_price', e.target.value)} />
                  <Button size="icon" variant="ghost" className="col-span-1" onClick={() => removeItem(i)} disabled={form.items.length === 1}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <div className="mt-2 text-right text-lg font-semibold tabular-nums">Total: {fmtCurrency(total, form.currency)}</div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy || !form.client_name} data-testid="invoice-submit-button" className="bg-[hsl(var(--brand-navy))]">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
